import React from 'react';
import { styled } from '@mui/material/styles';
import { Typography, Box, Paper, Tooltip, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import StarOutlineRounded from '@mui/icons-material/StarOutlineRounded';
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import AccessibleOutlinedIcon from '@mui/icons-material/AccessibleOutlined';
import GradingIcon from '@mui/icons-material/Grading';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import PassengerPreferencesDialog from './PassengerPreferencesDialog';
import {
  DEFAULT_SERVICE_LEVEL,
  PassengerPreference,
  PassengerPreferenceBC,
} from '../../../data/models/passenger.model';

const PREFIX = 'PassengerPreferences';

const classes = {
  container: `${PREFIX}-container`,
  details: `${PREFIX}-details`,
  header: `${PREFIX}-header`,
  row: `${PREFIX}-row`,
  iconAndText: `${PREFIX}-icon-and-text`,
  label: `${PREFIX}-label`,
  value: `${PREFIX}-value`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    gap: theme.spacing(4),
  },

  [`& .${classes.details}`]: {
    padding: theme.spacing(3, 4),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    paddingBottom: theme.spacing(1),
  },

  [`& .${classes.row}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(4),
  },

  [`& .${classes.iconAndText}`]: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },

  [`& .${classes.label}`]: {
    fontSize: 14,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: '143%',
    letterSpacing: '0.17px',
    color: theme.palette.lightTextPrimary.main,
  },

  [`& .${classes.value}`]: {
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '157%',
    letterSpacing: '0.1px',
    textAlign: 'right',
    color: theme.palette.lightTextPrimary.main,
  },
}));

interface PassengerPreferencesProps {
  passengerUUID: string;
  passengerPreferences: PassengerPreference[];
}

export default function PassengerPreferences({
  passengerUUID,
  passengerPreferences,
}: PassengerPreferencesProps) {
  const t = useCommonTranslation();
  const [preferenceDialogOpen, setPreferenceDialogOpen] = React.useState(false);

  const editPreference = () => {
    setPreferenceDialogOpen(true);
  };

  const getPreferenceValue = (attrBC: string, dflt = '') => {
    const pref = passengerPreferences.find(
      (pp) => pp.passengerProfileAttributeTypeBC === attrBC,
    );
    return pref?.attributeValue || dflt;
  };

  return (
    <StyledBox className={classes.container}>
      <Paper elevation={2} className={classes.details}>
        <Box className={classes.header}>
          <Typography variant="h3">{t('passenger.preferences')}</Typography>
          <Tooltip title={t('passenger.editPreferences')}>
            <span>
              <IconButton
                data-testid="delete-address"
                onClick={(evt) => editPreference()}
                color="primary">
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <PeopleAltIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.communication')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {getPreferenceValue(PassengerPreferenceBC.communication)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <GradingIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.language')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {getPreferenceValue(PassengerPreferenceBC.language)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <DirectionsCarFilledOutlinedIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.provider')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {getPreferenceValue(PassengerPreferenceBC.provider)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <AccessibleOutlinedIcon color="info" />
            <Typography className={classes.label}>
              {t('passenger.serviceLevel')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {getPreferenceValue(PassengerPreferenceBC.serviceLevel, DEFAULT_SERVICE_LEVEL)}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Box className={classes.iconAndText}>
            <StarOutlineRounded color="info" />
            <Typography className={classes.label}>
              {t('passenger.vip')}
            </Typography>
          </Box>
          <Typography className={classes.value}>
            {getPreferenceValue(PassengerPreferenceBC.vip)}
          </Typography>
        </Box>
      </Paper>
      <PassengerPreferencesDialog
        passengerUUID={passengerUUID}
        passengerPreferences={passengerPreferences}
        open={preferenceDialogOpen}
        close={() => setPreferenceDialogOpen(false)}
      />
    </StyledBox>
  );
}
